import { type FC, useCallback, useEffect, useRef, useState } from 'react';
import { NodeType, NODE_TYPE_COLORS } from '@/types/node.types';

interface CanvasToolbarProps {
  onAddNode: (nodeType: NodeType) => void;
  onAddZone?: () => void;
}

const toolbarItems = [
  { type: NodeType.Vision, label: 'Vision', icon: '\u2605' },
  { type: NodeType.Objective, label: 'Objective', icon: '\u25CE' },
  { type: NodeType.Activity, label: 'Activity', icon: '\u26A1' },
  { type: NodeType.Risk, label: 'Risk', icon: '\u26A0' },
  { type: NodeType.Opportunity, label: 'Opportunity', icon: '\u2728' },
  { type: NodeType.Resource, label: 'Resource', icon: '\u2699' },
  { type: NodeType.KPI, label: 'KPI', icon: '\u{1F4CA}' },
  { type: NodeType.Stakeholder, label: 'Stakeholder', icon: '\u{1F465}' },
];

/**
 * The toolbar always persists its position. `locked` is a separate concern:
 * when true the handle disables dragging but the position stays put.
 * `null` for x/y means "use default top-center anchor" — only the first
 * render before the user has ever moved it.
 */
interface ToolbarState {
  x: number | null;
  y: number | null;
  locked: boolean;
}

const STORAGE_KEY = 'stratmap.canvas-toolbar';

function loadState(): ToolbarState {
  if (typeof window === 'undefined') return { x: null, y: null, locked: false };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { x: null, y: null, locked: false };
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      const x = typeof parsed.x === 'number' ? parsed.x : null;
      const y = typeof parsed.y === 'number' ? parsed.y : null;
      const locked = parsed.locked === true;
      return { x, y, locked };
    }
  } catch {
    /* ignore */
  }
  return { x: null, y: null, locked: false };
}

function saveState(state: ToolbarState) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore */
  }
}

export const CanvasToolbar: FC<CanvasToolbarProps> = ({ onAddNode, onAddZone }) => {
  const [state, setState] = useState<ToolbarState>(() => loadState());
  const containerRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(
    null,
  );
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    saveState(state);
  }, [state]);

  const handleClick = useCallback(
    (nodeType: NodeType) => {
      onAddNode(nodeType);
    },
    [onAddNode],
  );

  // Toggle lock only — never move the toolbar.
  const toggleLock = useCallback(() => {
    setState((prev) => {
      // If user hasn't moved yet and wants to lock, freeze at current visual spot
      if (!prev.locked && (prev.x === null || prev.y === null) && containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const parent = containerRef.current.offsetParent as HTMLElement | null;
        const parentRect = parent?.getBoundingClientRect() ?? { left: 0, top: 0 };
        return {
          x: rect.left - parentRect.left,
          y: rect.top - parentRect.top,
          locked: true,
        };
      }
      return { ...prev, locked: !prev.locked };
    });
  }, []);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (state.locked) return;
      // Snapshot the current visual position to start dragging from exactly there
      let origX = state.x;
      let origY = state.y;
      if ((origX === null || origY === null) && containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const parent = containerRef.current.offsetParent as HTMLElement | null;
        const parentRect = parent?.getBoundingClientRect() ?? { left: 0, top: 0 };
        origX = rect.left - parentRect.left;
        origY = rect.top - parentRect.top;
      }
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        origX: origX ?? 0,
        origY: origY ?? 0,
      };
      setDragging(true);
      e.preventDefault();
    },
    [state],
  );

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e: MouseEvent) => {
      if (!dragRef.current) return;
      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;
      const nx = Math.max(0, Math.min(window.innerWidth - 80, dragRef.current.origX + dx));
      const ny = Math.max(0, Math.min(window.innerHeight - 40, dragRef.current.origY + dy));
      setState((prev) => ({ ...prev, x: nx, y: ny }));
    };
    const onUp = () => {
      setDragging(false);
      dragRef.current = null;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [dragging]);

  const useDefaultAnchor = state.x === null || state.y === null;
  const containerStyle: React.CSSProperties = useDefaultAnchor
    ? {
        position: 'absolute',
        top: 'var(--spacing-md)',
        left: '50%',
        transform: 'translateX(-50%)',
      }
    : {
        position: 'absolute',
        top: state.y ?? 0,
        left: state.x ?? 0,
      };

  return (
    <div
      ref={containerRef}
      className="canvas-toolbar"
      style={{
        ...containerStyle,
        zIndex: 10,
        display: 'flex',
        alignItems: 'stretch',
        gap: 'var(--spacing-xs)',
        padding: 'var(--spacing-sm)',
        backgroundColor: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-lg)',
        userSelect: 'none',
      }}
    >
      {/* Drag handle (disabled when locked) */}
      <div
        onMouseDown={handleMouseDown}
        title={state.locked ? 'Déverrouillez pour déplacer' : 'Glisser pour déplacer'}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 14,
          cursor: state.locked ? 'default' : dragging ? 'grabbing' : 'grab',
          color: 'var(--color-text-muted)',
          opacity: state.locked ? 0.25 : 0.7,
          fontSize: 12,
          lineHeight: 1,
        }}
      >
        ⋮⋮
      </div>

      {toolbarItems.map((item) => (
        <button
          key={item.type}
          onClick={() => handleClick(item.type)}
          title={`Add ${item.label} node`}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            padding: 'var(--spacing-xs) var(--spacing-sm)',
            backgroundColor: 'transparent',
            border: '1px solid transparent',
            borderRadius: 'var(--radius-md)',
            color: 'var(--color-text)',
            cursor: 'pointer',
            transition: 'var(--transition-fast)',
            minWidth: 56,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = NODE_TYPE_COLORS[item.type];
            e.currentTarget.style.backgroundColor = `${NODE_TYPE_COLORS[item.type]}15`;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = 'transparent';
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <span style={{ fontSize: 'var(--font-size-lg)', color: NODE_TYPE_COLORS[item.type] }}>
            {item.icon}
          </span>
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
            {item.label}
          </span>
        </button>
      ))}

      {/* Divider */}
      <div
        style={{
          width: 1,
          alignSelf: 'stretch',
          backgroundColor: 'var(--color-border)',
          margin: '0 var(--spacing-xs)',
        }}
      />

      {/* Zone button */}
      {onAddZone && (
        <button
          onClick={onAddZone}
          title="Ajouter une zone"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            padding: 'var(--spacing-xs) var(--spacing-sm)',
            backgroundColor: 'transparent',
            border: '1px solid transparent',
            borderRadius: 'var(--radius-md)',
            color: 'var(--color-text)',
            cursor: 'pointer',
            transition: 'var(--transition-fast)',
            minWidth: 56,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = 'var(--color-border)';
            e.currentTarget.style.backgroundColor = 'var(--color-background)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = 'transparent';
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <span style={{ fontSize: 'var(--font-size-lg)', color: 'var(--color-text-muted)' }}>
            &#9638;
          </span>
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
            Zone
          </span>
        </button>
      )}

      {/* Lock toggle */}
      <div
        style={{
          width: 1,
          alignSelf: 'stretch',
          backgroundColor: 'var(--color-border)',
          margin: '0 var(--spacing-xs)',
        }}
      />
      <button
        onClick={toggleLock}
        title={
          state.locked
            ? 'Déverrouiller (autoriser le déplacement)'
            : 'Verrouiller à la position actuelle'
        }
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 8px',
          backgroundColor: 'transparent',
          border: '1px solid transparent',
          borderRadius: 'var(--radius-md)',
          color: state.locked ? 'var(--color-primary, #3B82F6)' : 'var(--color-text-muted)',
          cursor: 'pointer',
          fontSize: 14,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = 'var(--color-border)';
          e.currentTarget.style.backgroundColor = 'var(--color-background)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = 'transparent';
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        {state.locked ? '📌' : '📍'}
      </button>
    </div>
  );
};

export default CanvasToolbar;
